import { Button } from "@dropbox/dig-components/buttons";
import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  ShareArrowLine,
  ShareLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Change_StackUserPermissions } from "@mirage/analytics/events/types/change_stack_user_permissions";
import { PAP_Select_DashShareRecipient } from "@mirage/analytics/events/types/select_dash_share_recipient";
import { PAP_Send_DashShareModal } from "@mirage/analytics/events/types/send_dash_share_modal";
import { PAP_Shown_DashCard } from "@mirage/analytics/events/types/shown_dash_card";
import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { tagged } from "@mirage/service-logging";
import {
  MetricPageName,
  StackDetailsModule,
} from "@mirage/service-operational-metrics/module/constants";
import { useRecordModuleLatency } from "@mirage/service-operational-metrics/module/module";
import {
  getStacksDataCachedTags,
  useStacksDataCachedTags,
} from "@mirage/service-operational-metrics/module/tags";
import { useIsPublicSharingAllowed } from "@mirage/service-stack-admin-settings/hooks";
import { previewStack } from "@mirage/service-stacks";
import {
  stackDerivePAPProps,
  stackGetShareId,
} from "@mirage/service-stacks/service/utils";
import { useIsDfbUser } from "@mirage/shared/hooks/useIsDfbUser";
import { showSnackbar } from "@mirage/shared/snackbar";
import {
  CardHeaderType,
  TwoColumnGridCard,
} from "@mirage/shared/two-column-grid/TwoColumnGridCard";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";
import { nonNil } from "@mirage/shared/util/tiny-utils";
import { membersFromStack } from "@mirage/stacks/Helpers/Utils";
import {
  addOrUpdateSharingMembers,
  getSharingMembers,
  getSharingUserContacts,
  removeSharingMember,
} from "@mirage/stacks/ShareModal/Api";
import { AvatarMember } from "@mirage/stacks/ShareModal/Avatar";
import { useShareModal } from "@mirage/stacks/ShareModal/ShareModal";
import { SharingTypeahead } from "@mirage/stacks/ShareModal/SharingTypeahead";
import { StackPermission } from "@mirage/stacks/ShareModal/Types";
import {
  coerceStackAccessLevel,
  sharingMemberKeysMatch,
  stackMemberFromSharingMember,
  validateEmail,
} from "@mirage/stacks/ShareModal/Utils";
import { getTheme } from "@mirage/stacks/themes";
import { transparentTheme } from "@mirage/stacks/themes/themeDefinitions";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import classnames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./SharingModule.module.css";

import type { stacks } from "@dropbox/api-v2-client/types";
import type { StackSendShareType } from "@mirage/analytics/events/enums/stack_send_share_type";
import { useStackChatEnabled } from "@mirage/service-experimentation/useStackChatEnabled";
import type {
  SharingMember,
  SharingMemberKey,
  SharingUserContact,
  SharingUserContactKey,
} from "@mirage/stacks/ShareModal/Types";
import { SharingModuleAccessMembers } from "./SharingModuleAccessMembers";

const featureLine = "stacks";
const actionSurfaceComponent = "sharing_module";
const logger = tagged("SharingModule");

type SharingModuleProps = {
  stack: stacks.Stack;
};

export const SharingModule: React.FC<SharingModuleProps> = ({ stack }) => {
  const [sharingContacts, setSharingContacts] = useState<SharingUserContact[]>(
    [],
  );

  const { reportPapEvent } = useMirageAnalyticsContext();
  const isPublicSharingAllowed = useIsPublicSharingAllowed();
  const [isSharingViaTextField, setIsSharingViaTextField] = useState(false);
  const [membersBeingSharedWith, setMembersBeingSharedWith] = useState<
    SharingMemberKey[]
  >([]);
  const [membersThatWereSharedWith, setMembersThatWereSharedWith] = useState<
    SharingMemberKey[]
  >([]);
  const { openShareModal, shareModalJsx } = useShareModal({ stack });
  const hasLoggedImpression = useRef(false);
  const augustRevisionEnabled = useStackPageAugustRevisionEnabled();
  const isStackChatEnabled = useStackChatEnabled();
  const isDfbUser = useIsDfbUser();
  const groupSharingEnabled = augustRevisionEnabled && isDfbUser;

  const members = membersFromStack(stack);

  const namespaceId = useMemo(
    () => nonNil(stack?.namespace_id, "namespace_id"),
    [stack],
  );
  const shareId = useMemo(
    () => nonNil(stackGetShareId(stack), "shareId"),
    [stack],
  );
  const reloadStack = useCallback(() => {
    setTimeout(() => previewStack(shareId, { refresh: true }), 500);
  }, [shareId]);

  const markSharingModuleLoadComplete = useSharingModulePerformanceTracking();

  const handleGetSharingMembers = useCallback(
    async (searchText: string) => {
      return await getSharingMembers(searchText, groupSharingEnabled);
    },
    [groupSharingEnabled],
  );

  // Get suggestions and filter out members already on the stack or
  // members that we just shared with.
  useEffect(() => {
    getSharingUserContacts("")
      .then((result) => {
        const suggestedContacts = result.filter(
          (contact) =>
            // Only need to check for user member type since
            // getSharingUserContacts() only returns user members.
            !stack.sharing_data?.members?.some(
              (member) =>
                member.subject?.[".tag"] === "user" &&
                member.subject.email === contact.email,
            ) &&
            !membersThatWereSharedWith.some((member) =>
              sharingMemberKeysMatch(member, contact),
            ),
        );

        setSharingContacts(suggestedContacts);

        if (!hasLoggedImpression.current) {
          reportPapEvent(
            PAP_Shown_DashCard({
              actionSurfaceComponent,
              featureLine,
              dashCardType: "sharing_module",
              emptyState: suggestedContacts.length === 0,
            }),
          );
          hasLoggedImpression.current = true;
        }

        // Module load is complete when the sharing contacts are ready.
        markSharingModuleLoadComplete();

        return;
      })
      .catch((e) => {
        logger.error("Failed to get sharing user contacts", e);
      });
  }, [
    reportPapEvent,
    stack.sharing_data?.members,
    membersThatWereSharedWith,
    markSharingModuleLoadComplete,
  ]);

  const undoShare = async (member: SharingMemberKey) => {
    reportPapEvent(
      PAP_Change_StackUserPermissions({
        ...stackDerivePAPProps(stack),
        stackAccessLevel: coerceStackAccessLevel(
          stack.publish_data,
          stack.sharing_data?.shared_link?.access_level,
        ),
        featureLine,
        actionSurfaceComponent,
        isPublicSharingAllowed: isPublicSharingAllowed,
        stackSharedUserPermissionType: "remove",
      }),
    );

    const response = await removeSharingMember(namespaceId, member);

    setMembersThatWereSharedWith((currentMembers) =>
      currentMembers.filter(
        (currentMember) => !sharingMemberKeysMatch(currentMember, member),
      ),
    );

    if (response) {
      reloadStack();
    }
  };

  const handleMemberSelectedEvent = (
    share_type: StackSendShareType,
    count: number,
  ) => {
    reportPapEvent(
      PAP_Select_DashShareRecipient({
        ...stackDerivePAPProps(stack),
        featureLine,
        actionSurfaceComponent,
        stackSendShareType: share_type,
        isPublicSharingAllowed,
        numDashShareEmailPasted: count,
      }),
    );
  };

  const shareStack = async (
    member: SharingMember | SharingUserContactKey,
    fromTypeahead: boolean,
  ) => {
    if (member[".tag"] === "user" && !validateEmail(member.email)) {
      return;
    }

    if (fromTypeahead) {
      setIsSharingViaTextField(true);
    } else {
      setMembersBeingSharedWith((currentMembers) => [
        ...currentMembers,
        member,
      ]);
    }

    reportPapEvent(
      PAP_Send_DashShareModal({
        ...stackDerivePAPProps(stack),
        stackEmailNotificationsOn: true,
        stackPermissions: "editor",
        actionSurfaceComponent,
        featureLine,
        isPublicSharingAllowed,
        stackSendShareType: fromTypeahead
          ? "entered_by_user"
          : "suggested_collaborator",
        sharedWithGroup: member[".tag"] === "group",
        sharedWithExternal:
          member[".tag"] === "user" && !("displayName" in member),
      }),
    );
    publishEvent(SurveyEvent.ShareStack, {}, true);

    if (!fromTypeahead) {
      handleMemberSelectedEvent("suggested_collaborator", 1);
    }

    try {
      const result = await addOrUpdateSharingMembers(
        namespaceId,
        [{ memberKey: member, permission: StackPermission.WRITE }],
        true,
        "",
      );

      setMembersThatWereSharedWith((currentMembers) => [
        ...currentMembers,
        member,
      ]);

      // Group add results are returned as a nested object
      const memberResult =
        member[".tag"] === "user" ? result : result.group_result;
      if ((memberResult?.added?.length ?? 0) > 0) {
        showSnackbar({
          title: i18n.t("shared_with", {
            name: member[".tag"] === "user" ? member.email : member.displayName,
          }),
          buttons: [
            { label: i18n.t("undo"), onClick: () => undoShare(member) },
          ],
        });
        reloadStack();
      }
    } catch {
      showSnackbar({ title: i18n.t("failed_to_add_sharing_members") });
    } finally {
      if (fromTypeahead) {
        setIsSharingViaTextField(false);
      } else {
        setMembersBeingSharedWith((currentMembers) =>
          [...currentMembers].filter(
            (currentMemmber) => !sharingMemberKeysMatch(currentMemmber, member),
          ),
        );
      }
    }
  };

  const getGridTheme = () => {
    if (isStackChatEnabled) {
      return undefined;
    } else if (augustRevisionEnabled) {
      return transparentTheme;
    } else {
      return getTheme(
        stack?.stack_data?.color_index,
        stack?.stack_data?.custom_color,
      ).subtle;
    }
  };

  const isMemberSharingInProgress = (contact: SharingUserContact) => {
    return membersBeingSharedWith.some((member) =>
      sharingMemberKeysMatch(member, contact),
    );
  };

  return (
    <>
      <TwoColumnGridCard
        settingId={`stack_sharing:${namespaceId}`}
        cardTypeProps={{
          title: isStackChatEnabled
            ? i18n.t("num_members", { count: members.length })
            : i18n.t("share_this_stack"),
          cardType: CardHeaderType.INLINE_SUBTITLE,
          actionSurfaceComponent,
          featureLine,
        }}
        isAlwaysCollapsed={false}
        theme={getGridTheme()}
        className={classnames({
          [styles.augustRevision]: augustRevisionEnabled && !isStackChatEnabled,
          [styles.stackChatEnabled]: isStackChatEnabled,
        })}
        headerActions={
          !augustRevisionEnabled && (
            <Button
              variant="primary"
              onClick={openShareModal}
              withIconStart={<UIIcon src={ShareArrowLine} />}
            >
              {i18n.t("share")}
            </Button>
          )
        }
        showDividerLine={!isStackChatEnabled}
      >
        <div className={styles.bodyContainer}>
          <SharingModuleAccessMembers
            onShareSettingsClick={openShareModal}
            stack={stack}
          />
          <div className={styles.textInputContainer}>
            <SharingTypeahead
              stackMemberKeys={
                stack.sharing_data?.members?.map(
                  stackMemberFromSharingMember,
                ) ?? []
              }
              onMemberSelected={(member) => shareStack(member, true)}
              onMemberSelectedEvent={handleMemberSelectedEvent}
              isSharing={isSharingViaTextField}
              excludedMembers={membersThatWereSharedWith}
              getSharingMembers={handleGetSharingMembers}
              useStackChatRedesign={isStackChatEnabled}
            />
          </div>
          {sharingContacts.length > 0 && (
            <>
              <div className={styles.defaultPadding}>
                <Text
                  size={isStackChatEnabled ? "medium" : "small"}
                  isBold={!isStackChatEnabled}
                >
                  {isStackChatEnabled
                    ? i18n.t("team_invite_suggested_people")
                    : i18n.t("suggested_collaborators")}
                </Text>
              </div>
              <div className={styles.sharingContactsContainer}>
                {sharingContacts.slice(0, 3).map((contact) => (
                  <div
                    key={contact.email}
                    className={styles.sharingContactContainer}
                    role="button"
                    tabIndex={0}
                    onClick={() => shareStack(contact, false)}
                    onKeyDown={onKeyDownCommitFn(() =>
                      shareStack(contact, false),
                    )}
                  >
                    <div className={styles.avatarNameContainer}>
                      <AvatarMember member={contact} avatarSize="small" />
                      <Text className={meticulousRedactedStringClass}>
                        {contact.displayName}
                      </Text>
                    </div>
                    <div
                      className={classnames(
                        styles.rightDecoration,
                        isMemberSharingInProgress(contact) &&
                          styles.sharingInProgress,
                      )}
                    >
                      {isMemberSharingInProgress(contact) ? (
                        <Spinner size="small" />
                      ) : (
                        <UIIcon
                          src={isStackChatEnabled ? ShareLine : AddLine}
                          color="var(--dig-color__text__subtle)"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </TwoColumnGridCard>
      {shareModalJsx}
    </>
  );
};

function useSharingModulePerformanceTracking() {
  const tags = useStacksDataCachedTags();

  const { markModuleLoadComplete } = useRecordModuleLatency(
    MetricPageName.STACK_DETAILS,
    StackDetailsModule.SHARING,
    tags || getStacksDataCachedTags,
  );

  return markModuleLoadComplete;
}
